import { FC } from 'react';

import { PunchupTag } from 'features/common/components/Punchup/primitives/display/PunchupTag';
import { TICKET_TYPE_FALLBACK_COLOR } from 'features/common/utils/ticketTypeColors';

/**
 * Neutral grey shown when a ticket type has no resolvable colour (an unknown/orphan type — e.g. a
 * deleted type still on an old row). Re-exported from the colour resolver util so the grey literal
 * lives in exactly one place. In practice callers pass a colour resolved via `useTicketTypeColors`,
 * which already substitutes a stable palette fallback for an unset colour — this is only the last
 * resort for a `null`/unknown colour.
 */
export const TICKET_TYPE_TAG_FALLBACK = TICKET_TYPE_FALLBACK_COLOR;

export interface PunchupTicketTypeTagProps {
	/** Ticket type display name. */
	name: string;
	/** The ticket type's hex colour (TicketType.Color). Falls back to neutral grey when absent. */
	color?: string | null;
	/** Optional hover tooltip; defaults to the name. */
	title?: string;
}

/**
 * PunchupTicketTypeTag — the single shared way to render a ticket TYPE as a colour-tinted
 * chip across Scanned Tickets, Attendee List, Comped, Transactions, and analytics legends.
 *
 * Thin wrapper over `PunchupTag size="sm"` (invariant 6 — never fork the chip). It centralises
 * the `sm` size choice and the per-type-colour fallback so every surface degrades identically
 * before a colour is set.
 */
export const PunchupTicketTypeTag: FC<PunchupTicketTypeTagProps> = ({ name, color, title }) => (
	<PunchupTag size="sm" label={name} background={color || TICKET_TYPE_TAG_FALLBACK} title={title ?? name} />
);
